// Split one distributor between the users working it — e.g. 43% / 57% when one
// distributor's 14 beats are worked 6 / 8 by two users. A user's target AND achievement
// (secondary + primary kg) on that distributor become that share of the distributor's.
import { useEffect, useState } from "react";
import { Loader2, Save, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";

export default function UserShares({ periodType, periodStart }) {
  const [q, setQ] = useState("");
  const [found, setFound] = useState([]);
  const [dist, setDist] = useState(null);
  const [rows, setRows] = useState(null);
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setFound([]); return undefined; }
    const t = setTimeout(() => {
      api.get("/distributor-targets", { params: { period_type: periodType, period_start: periodStart, q: q.trim(), page_size: 8 } })
        .then((r) => setFound(r.data.items || [])).catch(() => setFound([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, periodType, periodStart]);

  const open = async (d) => {
    setDist(d); setFound([]); setQ(""); setRows(null);
    try {
      const r = await api.get("/distributor-targets/shares", { params: { distributor_id: d.distributor_id } });
      setRows(r.data.items);
      setVals(Object.fromEntries(r.data.items.map((i) => [i.user_id, i.share_pct ?? ""])));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const total = (rows || []).reduce((s, r) => s + (vals[r.user_id] === "" || vals[r.user_id] == null ? 100 : Number(vals[r.user_id])), 0);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/distributor-targets/shares", { distributor_id: dist.distributor_id,
        shares: rows.map((r) => ({ user_id: r.user_id, share_pct: vals[r.user_id] === "" ? null : Number(vals[r.user_id]) })) });
      toast.success("Shares saved");
      open(dist);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Card className="border-neutral-200 rounded-2xl" data-testid="dt-shares">
      <CardContent className="p-4 space-y-3">
        <p className="text-xs text-neutral-600">
          When two users work one distributor, give each SE a share (%). ASM keeps the full distributor. Their target and their secondary / primary kg on that
          distributor become that share. Leave a share empty for the full 100%.
        </p>
        <div className="relative max-w-md">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-neutral-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find the distributor by name or code…"
                 className="pl-8 h-9" data-testid="dt-share-search" />
          {found.length > 0 && (
            <div className="absolute z-20 mt-1 w-full rounded-lg border bg-white shadow-lg max-h-64 overflow-auto">
              {found.map((d) => (
                <button key={d.distributor_id} type="button" onClick={() => open(d)}
                        className="block w-full text-left px-3 py-2 text-sm hover:bg-neutral-50">
                  {d.name} <span className="text-[11px] text-neutral-400 font-mono">{d.code}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {dist && (
          <div className="space-y-2">
            <div className="text-sm font-semibold">{dist.name} <span className="text-[11px] text-neutral-400 font-mono">{dist.code}</span></div>
            {!rows ? <Loader2 className="h-4 w-4 animate-spin text-neutral-400" /> : rows.length === 0 ? (
              <div className="text-sm text-neutral-500">No users are mapped to this distributor.</div>
            ) : (
              <>
                <div className="divide-y rounded-lg border">
                  {rows.map((r) => (
                    <div key={r.user_id} className="flex items-center justify-between gap-3 px-3 py-2">
                      <div><div className="text-sm font-medium">{r.name}</div><div className="text-[11px] text-neutral-400">{r.role_code}</div></div>
                      <div className="flex items-center gap-1.5">
                        <Input type="number" min="0" max="100" step="0.01" placeholder="100"
                               value={vals[r.user_id] ?? ""} onChange={(e) => setVals((v) => ({ ...v, [r.user_id]: e.target.value }))}
                               className="h-8 w-[90px]" data-testid={`dt-share-${r.user_id}`} />
                        <span className="text-sm text-neutral-500">%</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <span className={"text-xs " + (Math.round(total) === 100 ? "text-emerald-600" : "text-amber-600")}>
                    Total {Math.round(total * 100) / 100}% {Math.round(total) === 100 ? "" : "— usually the shares add up to 100%"}
                  </span>
                  <Button size="sm" onClick={save} disabled={saving} className="tulsi-gradient text-white" data-testid="dt-share-save">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Save shares
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
